/**
 * 受け取りの登録の埋め込み（Stripe Connect の embedded components・account-onboarding）を読み込む小さな口。
 *
 * WHY 埋め込みか（MASTER-PLAN「クリエイターは Stripe を意識しない」）: Stripe の画面へ飛ばさず、Kumo のページの中で本人確認と
 * 口座の入力を済ませる。サーバーは Accounts v2・`dashboard: none` の口座を作り、Account Session の client_secret だけを返す
 * （apps/server/src/modules/money/payees.ts・connect.ts）。
 *
 * WHY npm の @stripe/connect-js を入れないか: 依存を増やさない方針 + 中身は「公式の script を 1 本読む → init → create」だけ。
 * script は Stripe の配信元から読む決まり（自前で配ると PCI・更新の面で不可）なので、どのみち外部の読み込みは残る。
 *
 * 【要確認】この口は Stripe の公開ドキュメント（docs.stripe.com/connect/get-started-connect-embedded-components の
 *   「without npm」の手順: connect-js.stripe.com/v1.0/connect.js → window.StripeConnect.init({ publishableKey, fetchClientSecret })
 *   → instance.create('account-onboarding') → setOnExit）に沿って書いたが、2026-09-27 時点では外部アクセスをしない規約のため
 *   本物の Stripe では未検証。公開前に test の鍵（STRIPE_TEST_PUBLISHABLE_KEY / SECRET_KEY）で一度通すこと。
 */

const SCRIPT_SRC = 'https://connect-js.stripe.com/v1.0/connect.js';

interface ConnectElement extends HTMLElement {
  setOnExit?: (cb: () => void) => void;
}
interface ConnectInstance {
  create: (component: 'account-onboarding') => ConnectElement;
  logout?: () => Promise<void>;
}
interface StripeConnectGlobal {
  init: (params: {
    publishableKey: string;
    fetchClientSecret: () => Promise<string>;
    locale?: string;
    appearance?: { variables?: Record<string, string> };
  }) => ConnectInstance;
}

declare global {
  interface Window {
    StripeConnect?: StripeConnectGlobal;
  }
}

let loading: Promise<StripeConnectGlobal> | null = null;

function loadScript(): Promise<StripeConnectGlobal> {
  if (window.StripeConnect) return Promise.resolve(window.StripeConnect);
  loading ??= new Promise<StripeConnectGlobal>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => (window.StripeConnect ? resolve(window.StripeConnect) : reject(new Error('Connect.js loaded without StripeConnect')));
    s.onerror = () => {
      loading = null; // 次の試行で読み直す（一時的な通信断）
      reject(new Error('Could not load the verification form'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/**
 * container に登録のフォームを出す。onExit = 本人がフォームを閉じた / 終えた（画面は状態を読み直す）。
 * 戻り値で後片付け（別の画面へ移るとき）
 */
export async function mountOnboarding(
  container: HTMLElement,
  opts: { publishableKey: string; fetchClientSecret: () => Promise<string>; locale: string; onExit: () => void }
): Promise<() => void> {
  const sc = await loadScript();
  // 色はダッシュボードの地に合わせる（CSS 変数の値を読んで渡す。Stripe の既定の青を Kumo の画面に持ち込まない）
  const css = getComputedStyle(document.documentElement);
  const v = (name: string) => css.getPropertyValue(name).trim();
  const instance = sc.init({
    publishableKey: opts.publishableKey,
    fetchClientSecret: opts.fetchClientSecret,
    locale: opts.locale,
    appearance: {
      variables: {
        // 2026-10-18: --accent を --env（環境の色）/ --primary（橙）/ --mark（墨）に分けた。ここは前と同じ値（空色）のまま --env を読む。
        //   橙にしない理由: Stripe は colorPrimary をリンクの字にも使う = 白地の橙の字は 3:1 で読みにくい（売上は隠している間なので後で決める）
        colorPrimary: v('--env') || '#1f4fd6',
        colorBackground: v('--surface') || '#ffffff',
        colorText: v('--ink') || '#17160f',
        borderRadius: '6px'
      }
    }
  });
  const el = instance.create('account-onboarding');
  el.setOnExit?.(opts.onExit);
  container.replaceChildren(el);
  return () => {
    container.replaceChildren();
  };
}
